import { useEffect, useState } from "react";
import { Ticket, Plus, Copy, Ban, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { PaquetesContador } from "@/components/admin/PaquetesContador";
import { toast } from "sonner";

const ESTADO = {
  activo: { label: "Activo", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  usado: { label: "Usado", cls: "bg-slate-200 text-slate-600", icon: Clock },
  desactivado: { label: "Desactivado", cls: "bg-red-100 text-red-700", icon: XCircle },
};

export default function CodigosCortesia() {
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notas, setNotas] = useState("");
  const [generando, setGenerando] = useState(false);
  const [nuevo, setNuevo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/codigos-cortesia");
      setCodigos(data);
    } catch (e) { toast.error("No se pudieron cargar los códigos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post("/admin/codigos-cortesia", { notas: notas.trim() });
      setNuevo(data.codigo);
      setNotas("");
      toast.success("Código de cortesía generado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el código");
    } finally { setGenerando(false); }
  };

  const copiar = (codigo) => {
    navigator.clipboard?.writeText(codigo);
    toast.success(`Código ${codigo} copiado`);
  };

  const desactivar = async (codigo) => {
    try {
      await api.post(`/admin/codigos-cortesia/${codigo}/desactivar`, {});
      toast.success("Código desactivado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desactivar");
    }
  };

  const activos = codigos.filter((c) => c.estado === "activo").length;
  const usados = codigos.filter((c) => c.estado === "usado").length;

  return (
    <div data-testid="codigos-cortesia-page">
      <PageHeader
        title="Cortesías y paquetes"
        subtitle="Códigos de un solo uso: cortesía gratuita de FELVORA o paquetes de contador pagados por transferencia."
      />
      <div className="space-y-6 px-6 py-6 md:px-8">
        {/* ago-2026 · Fase 3 — Paquetes de contador (canal de venta pagado por transferencia).
            Vive en esta misma pantalla como un tipo distinto de cortesía. */}
        <PaquetesContador />

        {/* Generador */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Nota (opcional) <span className="font-normal text-slate-400">— ej. cliente o motivo de la cortesía</span>
              </label>
              <Input
                data-testid="cc-notas-input"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Ej. Cortesía para Crédito Solidario MX (acuerdo comercial)"
              />
            </div>
            <Button data-testid="cc-generar-btn" onClick={generar} disabled={generando} className="bg-primary hover:bg-sidebar-hover">
              {generando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Generar código
            </Button>
          </div>
          {nuevo && (
            <div data-testid="cc-nuevo-codigo" className="mt-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <div>
                <p className="text-xs text-emerald-700">Nuevo código generado — compártelo con tu cliente:</p>
                <p className="mt-0.5 font-mono text-lg font-bold tracking-widest text-emerald-900">{nuevo}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => copiar(nuevo)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                <Copy className="mr-1.5 h-4 w-4" /> Copiar
              </Button>
            </div>
          )}
        </div>

        {/* Resumen */}
        <div className="flex gap-4 text-sm text-slate-500">
          <span><b className="text-slate-900">{activos}</b> activos</span>
          <span><b className="text-slate-900">{usados}</b> usados</span>
          <span><b className="text-slate-900">{codigos.length}</b> en total</span>
        </div>

        {/* Lista */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Nota</th>
                <th className="px-4 py-3">Usado por</th>
                <th className="px-4 py-3">Creado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
              ) : codigos.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Aún no has generado códigos de cortesía.</td></tr>
              ) : codigos.map((c) => {
                const est = ESTADO[c.estado] || ESTADO.activo;
                const esPaquete = c.tipo === "paquete_contador";
                return (
                  <tr key={c.codigo} data-testid={`cc-row-${c.codigo}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold tracking-wide text-slate-900">{c.codigo}</span>
                        <button onClick={() => copiar(c.codigo)} className="text-slate-400 hover:text-slate-700" title="Copiar">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span data-testid={`cc-tipo-${c.codigo}`}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          esPaquete ? "bg-indigo-100 text-indigo-800" : "bg-sky-100 text-sky-800"}`}>
                        {esPaquete ? "Paquete contador (pagado)" : "Cortesía (gratis)"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${est.cls}`}>
                        <est.icon className="h-3 w-3" /> {est.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[220px] truncate text-slate-600" title={c.notas}>{c.notas || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.usado_por_nombre ? (
                        <span>{c.usado_por_nombre}<br /><span className="text-xs text-slate-400">{fdatetime(c.usado_en)}</span></span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{fdatetime(c.creado_en)}</td>
                    <td className="px-4 py-3 text-right">
                      {c.estado === "activo" && (
                        <TooltipButton tooltip="Desactivar este código de invitación (no se podrá usar más)"
                          data-testid={`cc-desactivar-${c.codigo}`} variant="ghost" size="sm"
                          onClick={() => desactivar(c.codigo)} className="text-red-600 hover:bg-red-50 hover:text-red-700">
                          <Ban className="mr-1.5 h-3.5 w-3.5" /> Desactivar
                        </TooltipButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
